import { useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CommitteeDraft } from "@/components/governance/CommitteeForm";
import { CommitteeSheetForm } from "@/components/governance/CommitteeSheetForm";
import { CommitteeTree } from "@/components/governance/CommitteeTree";
import { CommitteeDetailsModal } from "@/components/governance/CommitteeDetailsModal";
import { FrameworkMasterTable } from "@/components/governance/FrameworkMasterTable";
import { FrameworkWizard } from "./FrameworkWizard";
import { GovernanceGraphView } from "@/components/governance/GovernanceGraphView";
import { useGovernance } from "@/hooks/useGovernance";

export default function GovernanceDashboard() {
  const {
    getActiveFramework,
    getCommitteesByFramework,
    getTermsByFramework,
    getCommitteeById,
    members,
    updateCommittee,
  } = useGovernance();

  const activeFramework = getActiveFramework();
  const scopedCommittees = useMemo(
    () => (activeFramework ? getCommitteesByFramework(activeFramework.id) : []),
    [activeFramework, getCommitteesByFramework],
  );
  const scopedTerms = useMemo(
    () => (activeFramework ? getTermsByFramework(activeFramework.id) : []),
    [activeFramework, getTermsByFramework],
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editParentId, setEditParentId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingCommitteeId, setEditingCommitteeId] = useState<string | null>(null);
  const [selectedCommitteeId, setSelectedCommitteeId] = useState<string | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const editingCommittee = editingCommitteeId ? getCommitteeById(editingCommitteeId) : undefined;
  const selectedCommittee = selectedCommitteeId
    ? getCommitteeById(selectedCommitteeId)
    : undefined;
  const selectedCommitteeOwner = selectedCommittee?.ownerId
    ? members.find((member) => member.id === selectedCommittee.ownerId)
    : undefined;
  const selectedCommitteeChair = selectedCommittee?.chairpersonId
    ? members.find((member) => member.id === selectedCommittee.chairpersonId)
    : undefined;

  const openEditStructure = () => {
    setIsEditing(true);
    setEditParentId(null);
  };

  const openAddSubCommittee = (committeeId: string) => {
    setIsEditing(true);
    setEditParentId(committeeId);
  };

  const openEditCommittee = (committeeId: string) => {
    setEditingCommitteeId(committeeId);
    setSheetOpen(true);
  };

  const handleSaveCommittee = (draft: CommitteeDraft) => {
    if (!editingCommittee) return;
    updateCommittee(editingCommittee.id, {
      name: draft.name.trim() || editingCommittee.name,
      description: draft.description,
      ownerId: draft.ownerId,
      type: draft.type,
      memberIds: draft.memberIds,
    });
  };

  const openCommitteeDetails = (committeeId: string) => {
    setSelectedCommitteeId(committeeId);
    setDetailsModalOpen(true);
  };

  if (!activeFramework) {
    return (
      <div className="space-y-5">
        <div className="space-y-1">
          <h1 className="text-3xl font-medium text-foreground">Governance Structure</h1>
          <p className="text-muted-foreground">
            Set up your governance hierarchy. Add your board and committees, then publish when ready.
          </p>
        </div>
        <FrameworkWizard
          onPublish={() => {}}
          onSaveDraft={() => {}}
        />
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-5">
        <FrameworkWizard
          frameworkId={activeFramework.id}
          parentId={editParentId}
          onPublish={() => setIsEditing(false)}
          onSaveDraft={() => setIsEditing(false)}
          onCancel={() => setIsEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full space-y-6 overflow-x-hidden">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-medium text-foreground">{activeFramework.name}</h1>
          <p className="text-muted-foreground">{activeFramework.description}</p>
        </div>
        <Button onClick={openEditStructure} className="shrink-0">
          <Pencil className="mr-2 h-4 w-4" />
          Edit Structure
        </Button>
      </div>

      <Tabs defaultValue="graph" className="min-w-0 space-y-4">
        <TabsList>
          <TabsTrigger
            value="graph"
            className="data-[state=active]:bg-violet-600 data-[state=active]:text-white"
          >
            Graph View
          </TabsTrigger>
          <TabsTrigger
            value="table"
            className="data-[state=active]:bg-violet-600 data-[state=active]:text-white"
          >
            Table View
          </TabsTrigger>
          <TabsTrigger
            value="list"
            className="data-[state=active]:bg-violet-600 data-[state=active]:text-white"
          >
            List View
          </TabsTrigger>
        </TabsList>
        <TabsContent value="table" className="min-w-0">
          <Card className="min-w-0 shadow-card">
            <CardHeader>
              <CardTitle>Committee Structure</CardTitle>
            </CardHeader>
            <CardContent className="min-w-0">
              <FrameworkMasterTable
                committees={scopedCommittees}
                terms={scopedTerms}
                members={members}
                onAssignOwner={openEditCommittee}
                onAddSubCommittee={openAddSubCommittee}
                onEditCommittee={openEditCommittee}
                onViewCommittee={openCommitteeDetails}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="graph">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Governance Hierarchy Graph</CardTitle>
            </CardHeader>
            <CardContent>
              <GovernanceGraphView
                committees={scopedCommittees}
                selectedCommitteeId={selectedCommitteeId}
                onCommitteeSelect={openCommitteeDetails}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="list">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Governance Committee List</CardTitle>
            </CardHeader>
            <CardContent>
              <CommitteeTree
                committees={scopedCommittees}
                members={members}
                selectedCommitteeId={selectedCommitteeId}
                onSelect={openCommitteeDetails}
                onAddSubCommittee={(committee) => openAddSubCommittee(committee.id)}
                onEditCommittee={(committee) => openEditCommittee(committee.id)}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CommitteeSheetForm
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) setEditingCommitteeId(null);
        }}
        parentCommittee={editingCommittee?.parentId ? getCommitteeById(editingCommittee.parentId) : undefined}
        committeeToEdit={editingCommittee}
        members={members}
        onSave={handleSaveCommittee}
      />

      <CommitteeDetailsModal
        open={detailsModalOpen}
        onOpenChange={setDetailsModalOpen}
        committee={selectedCommittee}
        members={members}
        owner={selectedCommitteeOwner}
        chairperson={selectedCommitteeChair}
      />
    </div>
  );
}
